import React, { useState, useEffect } from "react";
import { FaChartBar, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../Baseurl";
import { useNavigate } from "react-router-dom";

export default function FinanceDashboard() {
  const [showFinancial, setShowFinancial] = useState(false);

  // Financial States
  const [balance, setBalance] = useState(0);
  const [targets, setTargets] = useState([]);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [expenseRecords, setExpenseRecords] = useState([]);
  const [summary, setSummary] = useState({ total_income: 0, total_expense: 0 });

  const [editTargetId, setEditTargetId] = useState(null);


  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
   useEffect(() => {
     const adminId = localStorage.getItem("adminID");
     if (!adminId) {
       navigate("/"); 
     }
 
     // 👇 load dark mode preference
     const savedTheme = localStorage.getItem("darkMode") === "true";
     setDarkMode(savedTheme);
     document.body.classList.toggle("dark", savedTheme);
   }, [navigate]);


  // Form Inputs
  const [newTarget, setNewTarget] = useState("");
  const [targetAmount, setTargetAmount] = useState("");
  const [targetMonth, setTargetMonth] = useState("");
  const [amount, setAmount] = useState("");
  const [transactionMonth, setTransactionMonth] = useState("");

  // Month Filter
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, "0")}`;
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);

  // Modals
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  // Org ID
  const orgId = localStorage.getItem("organization_id");

  // ✅ Fetch records on load & when selectedMonth changes
  useEffect(() => {
    fetchDashboardData(selectedMonth);
  }, [selectedMonth]);

  const fetchDashboardData = async (month) => {
    try {
      const res = await axios.get(
        `${baseurl}getFinancialDashboardRecords/${orgId}?month=${month}`
      );

      setTargets(res.data.allRecords || []);
      setIncomeRecords(res.data.incomeRecords || []);
      setExpenseRecords(res.data.expenseRecords || []);
      setSummary(res.data.summary || { total_income: 0, total_expense: 0 });

      setBalance(
        parseFloat(res.data.summary.total_income || 0) -
          parseFloat(res.data.summary.total_expense || 0)
      );
    } catch (err) {
      console.error("Error fetching dashboard:", err);
    }
  };

  // ✅ Change month with arrows
  const changeMonth = (direction) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const newDate = new Date(year, month - 1, 1);

    if (direction === "prev") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }

    const newMonth = `${newDate.getFullYear()}-${String(
      newDate.getMonth() + 1
    ).padStart(2, "0")}`;

    setSelectedMonth(newMonth);
  };

  // ✅ Format month (YYYY-MM to "Aug 2025")
  const formatMonth = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    return d.toLocaleString("default", { month: "short", year: "numeric" });
  };

  // ✅ Save (Insert/Update) Financial Target
  const handleSaveTarget = async () => {
    if (!newTarget || !targetAmount || !targetMonth) return;

    const payload = {
      id: editTargetId || null,
      org_id: orgId,
      category: "Target",
      amount: parseFloat(targetAmount),
      month_year: `${targetMonth}-01`,
      notes: newTarget,
    };

    await axios.post(`${baseurl}saveFinancialDashboardRecord`, payload);
    fetchDashboardData(selectedMonth);

    setNewTarget("");
    setTargetAmount("");
    setTargetMonth("");
    setEditTargetId(null);
    setShowTargetModal(false);
  };

  // ✅ Save Income / Expense
  const handleTransaction = async (type) => {
    if (!amount || !transactionMonth) return;
    const amt = parseFloat(amount);

    const payload = {
      org_id: orgId,
      type,
      category: type === "income" ? "General Income" : "General Expense",
      amount: amt,
      transaction_date: `${transactionMonth}-01`,
      notes: `${type} entry`,
    };

    await axios.post(`${baseurl}addIncomeExpense`, payload);
    fetchDashboardData(selectedMonth);
    setAmount("");
    setTransactionMonth("");
    setShowTransactionModal(false);
  };

  // ✅ Find target for selected month
  const currentTarget = targets.find(
    (t) => t.month_year && t.month_year.startsWith(selectedMonth)
  );

  const progress =
    currentTarget && currentTarget.amount > 0
      ? Math.min(
          ((summary.total_income || 0) / currentTarget.amount) * 100,
          100
        )
      : 0;

  return (
    <>
      <button
        className="btn btn-light mb-3"
        onClick={() => setShowFinancial(!showFinancial)}
      >
        <FaChartBar size={20} />
      </button>

      {/* Financial Dashboard Popup */}
      {showFinancial && (
        <div className={`financial-popup crdf2 ${darkMode ? "dark" : ""}`}>
          <h5 className={`mainttl ${darkMode ? "dark" : ""}`}>
            <b>Financial Dashboard</b>
          </h5>

          {/* Month Filter with Arrows */}
          <div className="d-flex align-items-center mb-3">
            <button
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={() => changeMonth("prev")}
            >
              <FaChevronLeft />
            </button>
            <h6 className="mb-0">{formatMonth(`${selectedMonth}-01`)}</h6>
            <button
              className="btn btn-sm btn-outline-secondary ms-2"
              onClick={() => changeMonth("next")}
            >
              <FaChevronRight />
            </button>
          </div>

          <div className="d-flex">
            {/* Balance */}
            <div className={`card p-3 mb-3 shadow-sm me-3 text-center pcard ${darkMode ? "dark" : ""}`} style={{minWidth: '190px'}}>
              <h6>Money in bank</h6>
              <h3 className={`mainttl ${darkMode ? "dark" : ""}`}>£{balance.toLocaleString()}</h3>
              {/* <p className="mb-1">
                Income: £{Number(summary.total_income).toLocaleString()}
              </p>
              <p className="mb-2">
                Expense: £{Number(summary.total_expense).toLocaleString()}
              </p> */}
              <button
                className={` btn mt-2 mainttl ${darkMode ? "dark" : ""}`}
                onClick={() => setShowTransactionModal(true)}
              >
                 Update Now
              </button>
            </div>

            {/* Targets + Income + Expense */}
            <div className={`card p-3 mb-3 shadow-sm flex-grow-1 pcard ${darkMode ? "dark" : ""}`}>
              <div className="d-flex justify-content-between mb-2">
                <h6>Your Targets</h6>
                <button
                  className={`btn new-project-btn d-flex align-items-center 
                    gap-1 px-3 py-2 tab4 mainttl ${darkMode ? "dark" : ""} `}
                  onClick={() => {
                    setEditTargetId(null);
                    setShowTargetModal(true);
                  }}
                >
                  + Add Target
                </button>
              </div>

              <ul className="list-unstyled mb-0">
                {/* Show Targets */}
                {targets.map((t) => (
                  <li key={`target-${t.id}`} className="mb-2">
                    🎯 {t.notes} (£{t.amount}) — {formatMonth(t.month_year)}
                    <button
                      className="btn btn-sm btn-link"
                      onClick={() => {
                        setEditTargetId(t.id);
                        setNewTarget(t.notes);
                        setTargetAmount(t.amount);
                        setTargetMonth(
                          t.month_year ? t.month_year.substring(0, 7) : ""
                        );
                        setShowTargetModal(true);
                      }}
                    >
                      Edit
                    </button>
                  </li>
                ))}

                {/* Show Income */}
                {incomeRecords.map((i) => (
                  <li key={`income-${i.id}`} className="mb-2 text-success">
                    ➕ {i.category}: £{i.amount} —{" "}
                    {formatMonth(i.transaction_date)}
                  </li>
                ))}

                {/* Show Expenses */}
                {expenseRecords.map((e) => (
                  <li key={`expense-${e.id}`} className="mb-2 text-danger">
                    ➖ {e.category}: £{e.amount} —{" "}
                    {formatMonth(e.transaction_date)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ✅ Progress Bar for Current Month Target */}
          {currentTarget ? (
            <div className={`card p-3 shadow-sm pcard ${darkMode ? "dark" : ""}`}>
              <h6>
                Financial Target {formatMonth(`${selectedMonth}-01`)}
              </h6>
              <div className="progress mb-2">
                <div
                  className="progress-bar bg-primary"
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                >
                  {Math.round(progress)}%
                </div>
              </div>
              <p className="mb-0">
                £{summary.total_income.toLocaleString()} of £
                {currentTarget.amount.toLocaleString()}
              </p>
            </div>
          ) : (
            <p className="text-muted mt-2">
              No financial target set for {formatMonth(`${selectedMonth}-01`)}.
            </p>
          )}

          <div
            className={`close-calendar mainttl ${darkMode ? "dark" : ""}`}
            onClick={() => setShowFinancial(false)}
          >
            Close Financial View
          </div>
        </div>
      )}

      {/* Target Modal */}
      {showTargetModal && (
        <div className="modal fade show d-block mdla1">
          <div className="modal-dialog">
            <div className={`modal-content pcard ${darkMode ? "dark" : ""}`}>
              <div className="modal-header">
                <h5 className={`mainttl ${darkMode ? "dark" : ""}`}>{editTargetId ? "Edit Target" : "Add Target"}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTargetModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Target description"
                  value={newTarget}
                  onChange={(e) => setNewTarget(e.target.value)}
                />
                <input
                  type="number"
                  className="form-control mb-2"
                  placeholder="Target amount"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                />
                <input
                  type="month"
                  className="form-control"
                  value={targetMonth}
                  onChange={(e) => setTargetMonth(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                {/* <button
                  className="btn btn-secondary"
                  onClick={() => setShowTargetModal(false)}
                >
                  Cancel
                </button> */}
                <button className={`btn new-project-btn d-flex align-items-center 
                    gap-1 px-3 py-2 tab4 mainttl ${darkMode ? "dark" : ""} `}
                     onClick={handleSaveTarget}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Income/Expense Modal */}
      {showTransactionModal && (
        <div className="modal fade show d-block mdla1">
          <div className="modal-dialog">
            <div className={`modal-content pcard ${darkMode ? "dark" : ""}`}>
              <div className="modal-header">
                <h5>Add Income / Expense</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTransactionModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <input
                  type="number"
                  className="form-control mb-2"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <input
                  type="month"
                  className="form-control"
                  value={transactionMonth}
                  onChange={(e) => setTransactionMonth(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-success"
                  onClick={() => handleTransaction("income")}
                >
                  Add Income
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleTransaction("expense")}
                >
                  Add Expense
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
